import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ArchivePageResponse,
  BulkUpdatePageTagsApplicationErrors,
  BulkUpdatePageTagsByFilterApplicationErrors,
  BulkUpdatePageTagsByFilterOptions,
  BulkUpdatePageTagsByFilterResponse,
  BulkUpdatePageTagsOptions,
  BulkUpdatePageTagsResponse,
  Page,
  PageQuery,
  PageRoute,
  PagesQueryBuilder,
  PublishPageOptions,
  PublishPageResponse,
  QueryPagesResponse,
  SchedulePageOptions,
  SchedulePageResponse,
  UnarchivePageResponse,
  UnpublishPageResponse,
  UpdatePage,
  archivePage as universalArchivePage,
  bulkUpdatePageTags as universalBulkUpdatePageTags,
  bulkUpdatePageTagsByFilter as universalBulkUpdatePageTagsByFilter,
  createPage as universalCreatePage,
  deletePage as universalDeletePage,
  getPage as universalGetPage,
  publishPage as universalPublishPage,
  queryPages as universalQueryPages,
  removePageRoute as universalRemovePageRoute,
  schedulePage as universalSchedulePage,
  setPageRoute as universalSetPageRoute,
  typedQueryPages as universalTypedQueryPages,
  unarchivePage as universalUnarchivePage,
  unpublishPage as universalUnpublishPage,
  updatePage as universalUpdatePage,
} from './quickpages-v1-page-pages.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/quick-pages' };

/** @internal */
export function createPage(httpClient: HttpClient): CreatePageSignature {
  return (page: Page) =>
    universalCreatePage(
      page,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePageSignature {
  /**
   * Creates a Page.
   * @param - Page to be created.
   * @returns The created Page.
   */
  (page: Page): Promise<
    NonNullablePaths<
      Page,
      | `originPageId`
      | `templateId`
      | `status`
      | `searchEngineIndexingEnabled`
      | `pageRoute.slug`
      | `pageRoute.urlType`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function getPage(httpClient: HttpClient): GetPageSignature {
  return (pageId: string) =>
    universalGetPage(
      pageId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPageSignature {
  /**
   * Retrieves a Page.
   * @param - ID of the Page to retrieve.
   * @returns The requested Page.
   */
  (pageId: string): Promise<
    NonNullablePaths<
      Page,
      | `originPageId`
      | `templateId`
      | `status`
      | `searchEngineIndexingEnabled`
      | `pageRoute.slug`
      | `pageRoute.urlType`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function updatePage(httpClient: HttpClient): UpdatePageSignature {
  return (_id: string, page: NonNullablePaths<UpdatePage, `revision`, 2>) =>
    universalUpdatePage(
      _id,
      page,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePageSignature {
  /**
   * Updates a Page.
   *
   *
   * Each time the page is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the page.
   * This ensures you're working with the latest page
   * and prevents unintended overwrites.
   * @param - Page ID.
   * @returns Updated Page.
   */
  (_id: string, page: NonNullablePaths<UpdatePage, `revision`, 2>): Promise<
    NonNullablePaths<
      Page,
      | `originPageId`
      | `templateId`
      | `status`
      | `searchEngineIndexingEnabled`
      | `pageRoute.slug`
      | `pageRoute.urlType`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function setPageRoute(httpClient: HttpClient): SetPageRouteSignature {
  return (pageId: string, pageRoute: PageRoute) =>
    universalSetPageRoute(
      pageId,
      pageRoute,
      // @ts-ignore
      { httpClient }
    );
}

interface SetPageRouteSignature {
  /**
   * Add a route for a page
   * @param - ID of the Page.
   * @param - Route to add.
   */
  (pageId: string, pageRoute: PageRoute): Promise<void>;
}

/** @internal */
export function removePageRoute(
  httpClient: HttpClient
): RemovePageRouteSignature {
  return (pageId: string) =>
    universalRemovePageRoute(
      pageId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemovePageRouteSignature {
  /**
   * Remove a route for a page
   * @param - ID of the Page.
   */
  (pageId: string): Promise<void>;
}

/** @internal */
export function publishPage(httpClient: HttpClient): PublishPageSignature {
  return (pageId: string, options?: PublishPageOptions) =>
    universalPublishPage(
      pageId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishPageSignature {
  /**
   * Publish a Page.
   * @param - ID of the Page to publish.
   */
  (pageId: string, options?: PublishPageOptions): Promise<
    NonNullablePaths<
      PublishPageResponse,
      | `page.originPageId`
      | `page.templateId`
      | `page.status`
      | `page.searchEngineIndexingEnabled`
      | `page.pageRoute.slug`
      | `page.pageRoute.urlType`
      | `page.tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function unpublishPage(httpClient: HttpClient): UnpublishPageSignature {
  return (pageId: string) =>
    universalUnpublishPage(
      pageId,
      // @ts-ignore
      { httpClient }
    );
}

interface UnpublishPageSignature {
  /**
   * Unpublish a page.
   * @param - ID of the Page to unpublish.
   */
  (pageId: string): Promise<
    NonNullablePaths<
      UnpublishPageResponse,
      | `page.originPageId`
      | `page.templateId`
      | `page.status`
      | `page.searchEngineIndexingEnabled`
      | `page.pageRoute.slug`
      | `page.pageRoute.urlType`
      | `page.tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function archivePage(httpClient: HttpClient): ArchivePageSignature {
  return (pageId: string) =>
    universalArchivePage(
      pageId,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchivePageSignature {
  /**
   * Archive a Page.
   * @param - ID of the Page to archive.
   */
  (pageId: string): Promise<
    NonNullablePaths<
      ArchivePageResponse,
      | `page.originPageId`
      | `page.templateId`
      | `page.status`
      | `page.searchEngineIndexingEnabled`
      | `page.pageRoute.slug`
      | `page.pageRoute.urlType`
      | `page.tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function unarchivePage(httpClient: HttpClient): UnarchivePageSignature {
  return (pageId: string) =>
    universalUnarchivePage(
      pageId,
      // @ts-ignore
      { httpClient }
    );
}

interface UnarchivePageSignature {
  /**
   * Unarchive a Page.
   * @param - ID of the Page to archive.
   */
  (pageId: string): Promise<
    NonNullablePaths<
      UnarchivePageResponse,
      | `page.originPageId`
      | `page.templateId`
      | `page.status`
      | `page.searchEngineIndexingEnabled`
      | `page.pageRoute.slug`
      | `page.pageRoute.urlType`
      | `page.tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function schedulePage(httpClient: HttpClient): SchedulePageSignature {
  return (pageId: string, options?: SchedulePageOptions) =>
    universalSchedulePage(
      pageId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SchedulePageSignature {
  /**
   * Schedule a Page for publishing at a future date.
   * @param - ID of the Page to schedule.
   */
  (pageId: string, options?: SchedulePageOptions): Promise<
    NonNullablePaths<
      SchedulePageResponse,
      | `page.originPageId`
      | `page.templateId`
      | `page.status`
      | `page.searchEngineIndexingEnabled`
      | `page.pageRoute.slug`
      | `page.pageRoute.urlType`
      | `page.tags.privateTags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function deletePage(httpClient: HttpClient): DeletePageSignature {
  return (pageId: string) =>
    universalDeletePage(
      pageId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeletePageSignature {
  /**
   * Deletes a Page.
   * @param - Id of the Page to delete.
   */
  (pageId: string): Promise<void>;
}

/** @internal */
export function queryPages(httpClient: HttpClient): QueryPagesSignature {
  return () =>
    universalQueryPages(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPagesSignature {
  /**
   * Retrieves a list of Pages, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 1,000 Pages can be returned per request.
   *
   * To learn how to query Pages, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): PagesQueryBuilder;
}

export function typedQueryPages(
  httpClient: HttpClient
): TypedQueryPagesSignature {
  return (query: PageQuery) =>
    universalTypedQueryPages(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPagesSignature {
  /** */
  (query: PageQuery): Promise<
    NonNullablePaths<
      QueryPagesResponse,
      | `pages`
      | `pages.${number}.originPageId`
      | `pages.${number}.templateId`
      | `pages.${number}.status`
      | `pages.${number}.searchEngineIndexingEnabled`
      | `pages.${number}.pageRoute.slug`
      | `pages.${number}.pageRoute.urlType`,
      5
    >
  >;
}

/** @internal */
export function bulkUpdatePageTags(
  httpClient: HttpClient
): BulkUpdatePageTagsSignature {
  return (pageIds: string[], options?: BulkUpdatePageTagsOptions) =>
    universalBulkUpdatePageTags(
      pageIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePageTagsSignature {
  /**
   * Synchronously update tags on multiple Pages, by list of Pages ids
   * A tag that appears both in the list of assign and unassign tags, will be assigned
   * @param - List of NileProtoTagsEntities that their tags will update.
   */
  (pageIds: string[], options?: BulkUpdatePageTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdatePageTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdatePageTagsApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkUpdatePageTagsByFilter(
  httpClient: HttpClient
): BulkUpdatePageTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdatePageTagsByFilterOptions
  ) =>
    universalBulkUpdatePageTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePageTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple Pages, by provided filter
   * An empty filter will update all Pages
   * A tag that appears both in the list of assign and unassign tags, will be assigned
   * @param - Filter
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdatePageTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdatePageTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdatePageTagsByFilterApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  ApplicationError,
  ArchivePageRequest,
  ArchivePageResponse,
  BulkActionMetadata,
  BulkUpdatePageTagsByFilterOptions,
  BulkUpdatePageTagsByFilterRequest,
  BulkUpdatePageTagsByFilterResponse,
  BulkUpdatePageTagsOptions,
  BulkUpdatePageTagsRequest,
  BulkUpdatePageTagsResponse,
  BulkUpdatePageTagsResult,
  CreatePageRequest,
  CreatePageResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeletePageRequest,
  DeletePageResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetPageRequest,
  GetPageResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  Page,
  PageArchived,
  PageOriginOneOf,
  PagePublished,
  PageQuerySpec,
  PageRoute,
  PageScheduled,
  PageUnarchived,
  PageUnpublished,
  PagesQueryBuilder,
  PagesQueryResult,
  PublishPageOptions,
  PublishPageRequest,
  PublishPageResponse,
  QueryPagesRequest,
  QueryPagesResponse,
  RemovePageRouteRequest,
  RemovePageRouteResponse,
  RestoreInfo,
  SchedulePageOptions,
  SchedulePageRequest,
  SchedulePageResponse,
  SetPageRouteRequest,
  SetPageRouteResponse,
  SortOrder,
  Sorting,
  Status,
  TagList,
  Tags,
  Type,
  UnarchivePageRequest,
  UnarchivePageResponse,
  UnpublishPageRequest,
  UnpublishPageResponse,
  UpdatePage,
  UpdatePageRequest,
  UpdatePageResponse,
  WebhookIdentityType,
  utils,
} from './quickpages-v1-page-pages.universal.js';
